import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Star, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { Field, Input, Textarea, Btn, Toggle, Modal } from "@/components/admin/AdminUI";
import ImageUpload from "@/components/admin/ImageUpload";

const EMPTY = { title: "", edition: "", description: "", cover_image: "", issuu_url: "", date: "", featured: false, published: true, order: 0 };

export default function MagazinesAdmin() {
  const [mags, setMags] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/magazines?published_only=false").then((r) => setMags(r.data || []));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, order: mags.length }); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ ...EMPTY, ...m }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title) { toast.error("El título es obligatorio"); return; }
    const payload = { ...form, order: Number(form.order) || 0 };
    try {
      if (editing) await api.put(`/admin/magazines/${editing.id}`, payload);
      else await api.post("/admin/magazines", payload);
      toast.success("Revista guardada"); setOpen(false); load();
    } catch { toast.error("Error al guardar"); }
  };
  const del = async (id) => { if (!window.confirm("¿Eliminar revista?")) return; await api.delete(`/admin/magazines/${id}`); toast.success("Eliminada"); load(); };

  return (
    <div>
      <header className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-4xl tracking-tight">Revistas</h1>
        <Btn variant="accent" onClick={openNew} data-testid="new-magazine-btn"><Plus size={15} /> Nueva edición</Btn>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {mags.map((m) => (
          <div key={m.id} className="bg-white border border-sunset-border" data-testid={`magazine-card-${m.id}`}>
            <div className="aspect-[3/4] bg-sunset-border overflow-hidden relative">
              {m.cover_image && <img src={mediaUrl(m.cover_image)} alt="" className="w-full h-full object-cover" />}
              {m.featured && <span className="absolute top-2 left-2 bg-sunset-accent text-white text-[10px] px-2 py-1 uppercase tracking-wider flex items-center gap-1"><Star size={10} /> Destacada</span>}
            </div>
            <div className="p-4">
              <p className="text-xs text-sunset-accent uppercase tracking-wider">{m.edition}</p>
              <p className="font-serif text-lg leading-tight mt-1 mb-2">{m.title}</p>
              <div className="flex items-center justify-between">
                <span className={`text-xs ${m.published ? "text-green-700" : "text-sunset-muted"}`}>{m.published ? "Publicada" : "Oculta"}</span>
                <div className="flex gap-1">
                  {m.issuu_url && <a href={m.issuu_url} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:text-sunset-accent"><ExternalLink size={15} /></a>}
                  <button onClick={() => openEdit(m)} className="p-1.5 hover:text-sunset-accent" data-testid={`edit-magazine-${m.id}`}><Edit size={15} /></button>
                  <button onClick={() => del(m.id)} className="p-1.5 hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar edición" : "Nueva edición"}>
        <div className="mb-4"><ImageUpload label="Portada" value={form.cover_image} onChange={(v) => set("cover_image", v)} /></div>
        <Field label="Título"><Input data-testid="magazine-title" value={form.title} onChange={(e) => set("title", e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Número / Edición"><Input value={form.edition} onChange={(e) => set("edition", e.target.value)} placeholder="Nº 01" /></Field>
          <Field label="Fecha"><Input value={form.date} onChange={(e) => set("date", e.target.value)} placeholder="2026" /></Field>
        </div>
        <Field label="Descripción"><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
        <Field label="URL de Issuu"><Input data-testid="magazine-issuu" value={form.issuu_url} onChange={(e) => set("issuu_url", e.target.value)} placeholder="https://issuu.com/..." /></Field>
        <Field label="Orden"><Input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} /></Field>
        <div className="flex gap-8 py-2 mb-4">
          <Toggle checked={form.published} onChange={(v) => set("published", v)} label="Publicada" />
          <Toggle checked={form.featured} onChange={(v) => set("featured", v)} label="Destacada" />
        </div>
        <Btn variant="accent" onClick={save} data-testid="save-magazine-btn">Guardar</Btn>
      </Modal>
    </div>
  );
}
